type NativeBridge = Readonly<{ postMessage: (message: string) => void }>;

function bridge(): NativeBridge | null {
  return (window as unknown as { ReactNativeWebView?: NativeBridge }).ReactNativeWebView ?? null;
}

export function postNativeMessage(message: Record<string, unknown>): void {
  bridge()?.postMessage(JSON.stringify(message));
}

export function isNativeMobileShell(): boolean {
  return bridge() !== null;
}

/**
 * Put Android into its communications audio mode before Chromium opens the
 * microphone. This lets the hardware AEC use the correct speaker/earpiece
 * route from the first captured frame instead of changing route afterwards.
 */
export async function prepareNativeCallAudio(video: boolean): Promise<void> {
  const native = bridge();
  if (!native) return;
  const requestId = globalThis.crypto?.randomUUID?.()
    ?? `audio-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  await new Promise<void>((resolve) => {
    let finished = false;
    const timeout = window.setTimeout(done, 3_000);
    function done(): void {
      if (finished) return;
      finished = true;
      window.clearTimeout(timeout);
      window.removeEventListener("jtuc:native-call-audio-ready", ready as EventListener);
      resolve();
    }
    const ready = (event: Event) => {
      const detail = (event as CustomEvent<{ requestId?: string }>).detail;
      if (detail?.requestId === requestId) done();
    };
    window.addEventListener("jtuc:native-call-audio-ready", ready as EventListener);
    native.postMessage(JSON.stringify({ type: "call:audio-session", active: true, video, requestId }));
  });
}

export function releaseNativeCallAudio(): void {
  postNativeMessage({ type: "call:audio-session", active: false });
}

export type NativeCallAudioRoute = "earpiece" | "speaker";

export function setNativeCallAudioRoute(route: NativeCallAudioRoute): void {
  postNativeMessage({ type: "call:audio-route", route });
}

type NativeScreenOffer = Readonly<{
  requestId?: string;
  sdp?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  message?: string;
}>;

type NativeScreenSession = {
  peer: RTCPeerConnection;
  requestId: string;
  stream: MediaStream;
  endedListener: EventListener | null;
  candidateListener: EventListener | null;
};

let nativeScreenSession: NativeScreenSession | null = null;

function waitForIceGathering(peer: RTCPeerConnection, timeoutMs = 6_000): Promise<void> {
  if (peer.iceGatheringState === "complete") return Promise.resolve();
  return new Promise(resolve => {
    let finished = false;
    const timer = window.setTimeout(done, timeoutMs);
    function done(): void {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      peer.removeEventListener("icegatheringstatechange", changed);
      resolve();
    }
    function changed(): void {
      if (peer.iceGatheringState === "complete") done();
    }
    peer.addEventListener("icegatheringstatechange", changed);
  });
}

export function releaseNativeScreenCapture(): void {
  const current = nativeScreenSession;
  if (!current) return;
  nativeScreenSession = null;
  postNativeMessage({ type: "screen-share:stop", requestId: current.requestId });
  if (current.endedListener) window.removeEventListener("jtuc:native-screen-ended", current.endedListener);
  if (current.candidateListener) window.removeEventListener("jtuc:native-screen-candidate", current.candidateListener);
  current.stream.getTracks().forEach((track) => track.stop());
  current.peer.close();
}

async function captureNativeDisplayMedia(): Promise<MediaStream> {
  const native = bridge();
  if (!native) throw new Error("Нативная демонстрация экрана недоступна");
  releaseNativeScreenCapture();
  const requestId = globalThis.crypto?.randomUUID?.()
    ?? `screen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  // This peer talks only to React Native on the same Android device.
  const peer = new RTCPeerConnection({ iceServers: [], iceCandidatePoolSize: 0 });
  const stream = new MediaStream();
  nativeScreenSession = { peer, requestId, stream, endedListener: null, candidateListener: null };

  return new Promise<MediaStream>((resolve, reject) => {
    let settled = false;
    let answerSent = false;
    let progressStage = "start";
    const pendingNativeCandidates: RTCIceCandidateInit[] = [];
    const timeout = window.setTimeout(() => {
      const hint = progressStage === "permission"
        ? "Android не завершил запрос разрешения на показ экрана"
        : progressStage === "captured"
          ? "Android захватил экран, но WebView не получил локальный поток"
          : "Телефон не передал изображение экрана вовремя";
      fail(hint);
    }, 45_000);
    const cleanupListeners = () => {
      window.clearTimeout(timeout);
      window.removeEventListener("jtuc:native-screen-offer", offered as EventListener);
      window.removeEventListener("jtuc:native-screen-error", errored as EventListener);
      window.removeEventListener("jtuc:native-screen-progress", progressed as EventListener);
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanupListeners();
      releaseNativeScreenCapture();
      reject(new Error(message));
    };
    const ended = (event: Event) => {
      const detail = (event as CustomEvent<NativeScreenOffer>).detail;
      if (detail?.requestId !== requestId) return;
      if (!settled) {
        fail("Демонстрация экрана остановлена");
        return;
      }
      stream.getTracks().forEach((track) => {
        track.stop();
        track.dispatchEvent(new Event("ended"));
      });
      releaseNativeScreenCapture();
    };
    const progressed = (event: Event) => {
      const detail = (event as CustomEvent<NativeScreenOffer & {stage?: string}>).detail;
      if (detail?.requestId === requestId && detail.stage) progressStage = detail.stage;
    };
    const errored = (event: Event) => {
      const detail = (event as CustomEvent<NativeScreenOffer>).detail;
      if (detail?.requestId !== requestId) return;
      fail(detail.message || "Android не разрешил захват экрана");
    };
    const candidateReceived = async (event: Event) => {
      const detail = (event as CustomEvent<NativeScreenOffer>).detail;
      if (detail?.requestId !== requestId || !detail.candidate) return;
      try {
        if (!peer.remoteDescription) pendingNativeCandidates.push(detail.candidate);
        else await peer.addIceCandidate(detail.candidate);
      } catch (error) {
        fail(error instanceof Error ? error.message : "Не удалось принять сетевой маршрут Android");
      }
    };
    const offered = async (event: Event) => {
      const detail = (event as CustomEvent<NativeScreenOffer>).detail;
      if (detail?.requestId !== requestId || !detail.sdp) return;
      try {
        await peer.setRemoteDescription(detail.sdp);
        for (const candidate of pendingNativeCandidates.splice(0)) await peer.addIceCandidate(candidate);
        await peer.setLocalDescription(await peer.createAnswer());
        // The native side also receives candidates through trickle events, but
        // the complete SDP is the reliable path when Android briefly suspends
        // WebView during the MediaProjection permission transition.
        await waitForIceGathering(peer);
        if (!peer.localDescription) throw new Error("Не сформирован ответ WebRTC");
        postNativeMessage({ type: "screen-share:answer", requestId, sdp: peer.localDescription.toJSON() });
        answerSent = true;
        finish();
      } catch (error) {
        fail(error instanceof Error ? error.message : "Не удалось связать Android с окном звонка");
      }
    };
    const finish = () => {
      if (settled || !answerSent || stream.getVideoTracks().length === 0) return;
      settled = true;
      cleanupListeners();
      stream.getVideoTracks()[0]?.addEventListener("ended", () => releaseNativeScreenCapture(), { once: true });
      resolve(stream);
    };
    peer.addEventListener("track", (trackEvent) => {
      if (trackEvent.track.kind !== "video") return;
      if (!stream.getTracks().some((track) => track.id === trackEvent.track.id)) stream.addTrack(trackEvent.track);
      finish();
    });
    peer.addEventListener("icecandidate", (event) => {
      if (!event.candidate) return;
      postNativeMessage({ type: "screen-share:candidate", requestId, candidate: event.candidate.toJSON() });
    });
    peer.addEventListener("connectionstatechange", () => {
      if ((peer.connectionState === "failed" || peer.connectionState === "closed") && !settled) {
        fail("Соединение с захватом экрана Android прервано");
      }
    });
    window.addEventListener("jtuc:native-screen-offer", offered as EventListener);
    window.addEventListener("jtuc:native-screen-error", errored as EventListener);
    window.addEventListener("jtuc:native-screen-progress", progressed as EventListener);
    window.addEventListener("jtuc:native-screen-candidate", candidateReceived as EventListener);
    if (nativeScreenSession?.requestId === requestId) {
      nativeScreenSession.endedListener = ended as EventListener;
      nativeScreenSession.candidateListener = candidateReceived as EventListener;
    }
    window.addEventListener("jtuc:native-screen-ended", ended as EventListener);
    native.postMessage(JSON.stringify({ type: "screen-share:start", requestId }));
  });
}

export async function captureDisplayMedia(): Promise<MediaStream> {
  if (isNativeMobileShell()) return captureNativeDisplayMedia();
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("Этот браузер не поддерживает демонстрацию экрана");
  }
  return navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 30, max: 60 }, width: { ideal: 1_920 }, height: { ideal: 1_080 } },
    audio: false,
  });
}
